import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Users, Plus, DollarSign, Bell, Calendar, Settings,
  Phone, Check, X, AlertCircle, Gift, Megaphone,
  TrendingUp, Clock, Send, Cake, CreditCard, QrCode,
  Link2, FileText, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL;

const ClienteJaPainel = () => {
  const { slug } = useParams();
  const [assinante, setAssinante] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [vendas, setVendas] = useState([]);
  const [parcelas, setParcelas] = useState([]);
  const [campanhas, setCampanhas] = useState([]);
  const [aniversariantes, setAniversariantes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modals
  const [showNovoCliente, setShowNovoCliente] = useState(false);
  const [showNovaVenda, setShowNovaVenda] = useState(false);
  const [showNovaCampanha, setShowNovaCampanha] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  
  // Forms
  const [novoCliente, setNovoCliente] = useState({
    nome: '', telefone: '', data_nascimento: '', consentimento_mensagens: true
  });
  const [novaVenda, setNovaVenda] = useState({
    cliente_id: '', produto: '', valor_total: '', parcelas: 1, data_primeiro_pagamento: ''
  });
  const [novaCampanha, setNovaCampanha] = useState({
    tipo: 'natal', nome: '', mensagem: '', desconto_tipo: '', desconto_valor: '',
    desconto_validade: '', item_especifico: '', publico: 'todos'
  });
  const [config, setConfig] = useState({
    modo_envio: 'semi',
    zapi_instance_id: '',
    zapi_token: '',
    lembrete_3dias: true,
    lembrete_dia: true,
    lembrete_atraso: true,
    forma_cobranca: 'manual',
    pix_chave: '',
    pix_tipo: 'aleatoria',
    boleto_padrao: '',
    cartao_link: '',
    asaas_link: ''
  });

  // Stats
  const [stats, setStats] = useState({
    totalClientes: 0,
    aReceber: 0,
    vencendoHoje: 0,
    atrasadas: 0
  });

  useEffect(() => {
    loadData();
  }, [slug]);

  const loadData = async () => {
    try {
      const [assRes, cliRes, venRes, parRes, campRes, anivRes] = await Promise.all([
        fetch(`${API}/api/clienteja/assinantes/${slug}`),
        fetch(`${API}/api/clienteja/assinantes/${slug}/clientes`),
        fetch(`${API}/api/clienteja/assinantes/${slug}/vendas`),
        fetch(`${API}/api/clienteja/assinantes/${slug}/parcelas`),
        fetch(`${API}/api/clienteja/assinantes/${slug}/campanhas`),
        fetch(`${API}/api/clienteja/assinantes/${slug}/aniversarios?dias=7`)
      ]);

      const [assData, cliData, venData, parData, campData, anivData] = await Promise.all([
        assRes.json(),
        cliRes.json(),
        venRes.json(),
        parRes.json(),
        campRes.ok ? campRes.json() : [],
        anivRes.ok ? anivRes.json() : []
      ]);

      setAssinante(assData);
      setClientes(cliData);
      setVendas(venData);
      setParcelas(parData);
      setCampanhas(campData);
      setAniversariantes(anivData);

      // Load config from assinante
      if (assData) {
        setConfig({
          modo_envio: assData.modo_envio || 'semi',
          zapi_instance_id: assData.zapi_instance_id || '',
          zapi_token: assData.zapi_token || '',
          lembrete_3dias: assData.lembrete_3dias !== false,
          lembrete_dia: assData.lembrete_dia !== false,
          lembrete_atraso: assData.lembrete_atraso !== false,
          forma_cobranca: assData.forma_cobranca || 'manual',
          pix_chave: assData.pix_chave || '',
          pix_tipo: assData.pix_tipo || 'aleatoria',
          boleto_padrao: assData.boleto_padrao || '',
          cartao_link: assData.cartao_link || '',
          asaas_link: assData.asaas_link || ''
        });
      }

      // Calculate stats
      const hoje = new Date().toISOString().split('T')[0];
      const pendentes = parData.filter(p => p.status === 'pendente' || p.status === 'atrasado');
      const vencendoHoje = pendentes.filter(p => p.data_vencimento === hoje);
      const atrasadas = pendentes.filter(p => p.data_vencimento < hoje);
      
      setStats({
        totalClientes: cliData.length,
        aReceber: pendentes.reduce((s, p) => s + (p.valor || 0), 0),
        vencendoHoje: vencendoHoje.length,
        atrasadas: atrasadas.length
      });
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleNovoCliente = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/clienteja/assinantes/${slug}/clientes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(novoCliente)
      });
      
      if (res.ok) {
        setShowNovoCliente(false);
        setNovoCliente({ nome: '', telefone: '', data_nascimento: '', consentimento_mensagens: true });
        loadData();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleNovaVenda = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/clienteja/assinantes/${slug}/vendas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...novaVenda,
          valor_total: parseFloat(novaVenda.valor_total),
          parcelas: parseInt(novaVenda.parcelas)
        })
      });
      
      if (res.ok) {
        setShowNovaVenda(false);
        setNovaVenda({ cliente_id: '', produto: '', valor_total: '', parcelas: 1, data_primeiro_pagamento: '' });
        loadData();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleNovaCampanha = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...novaCampanha,
        desconto_valor: novaCampanha.desconto_valor ? parseFloat(novaCampanha.desconto_valor) : null
      };
      
      const res = await fetch(`${API}/api/clienteja/assinantes/${slug}/campanhas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        setShowNovaCampanha(false);
        setNovaCampanha({ tipo: 'natal', nome: '', mensagem: '', desconto_tipo: '', desconto_valor: '', desconto_validade: '', item_especifico: '', publico: 'todos' });
        loadData();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSaveConfig = async () => {
    try {
      await fetch(`${API}/api/clienteja/assinantes/${slug}/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });
      setShowConfig(false);
      loadData();
    } catch (err) {
      console.error(err);
    }
  };

  const marcarComoPago = async (parcelaId) => {
    try {
      await fetch(`${API}/api/clienteja/assinantes/${slug}/parcelas/${parcelaId}/status?status=pago`, {
        method: 'PUT'
      });
      loadData();
    } catch (err) {
      console.error(err);
    }
  };

  const enviarLembrete = async (parcela, tipo = 'lembrete_dia') => {
    try {
      const res = await fetch(`${API}/api/clienteja/assinantes/${slug}/parcelas/${parcela.parcela_id}/lembrete?tipo=${tipo}`, {
        method: 'POST'
      });
      
      if (res.ok) {
        const data = await res.json();
        // Open WhatsApp
        window.open(data.whatsapp_url, '_blank');
        loadData();
      }
    } catch (err) {
      console.error(err);
      // Fallback
      const cliente = clientes.find(c => c.cliente_id === parcela.cliente_id);
      if (cliente) {
        const msg = `Olá, ${cliente.nome}! Passando pra lembrar do pagamento de R$ ${parcela.valor.toFixed(2)} que vence em ${formatDate(parcela.data_vencimento)}. 🙏`;
        const phone = cliente.telefone.replace(/\D/g, '');
        window.open(`https://wa.me/55${phone}?text=${encodeURIComponent(msg)}`, '_blank');
      }
    }
  };

  const executarCampanha = async (campanhaId) => {
    try {
      const res = await fetch(`${API}/api/clienteja/assinantes/${slug}/campanhas/${campanhaId}/executar`, {
        method: 'POST'
      });
      
      if (res.ok) {
        const data = await res.json();
        alert(`Campanha preparada para ${data.clientes_alcancados} clientes!`);
        
        // Open WhatsApp for each message (semi mode)
        if (data.modo_envio === 'semi') {
          data.mensagens.forEach((m, i) => {
            setTimeout(() => {
              const phone = m.telefone.replace(/\D/g, '');
              window.open(`https://wa.me/55${phone}?text=${encodeURIComponent(m.mensagem)}`, '_blank');
            }, i * 2000);
          });
        }
        loadData();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const enviarAniversario = (cliente) => {
    const msg = `${cliente.nome.split(' ')[0]}! Feliz aniversário 🎉 Que seu dia seja leve e abençoado!`;
    const phone = cliente.telefone.replace(/\D/g, '');
    window.open(`https://wa.me/55${phone}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);
  };

  const getClienteName = (clienteId) => {
    const c = clientes.find(c => c.cliente_id === clienteId);
    return c?.nome || 'Cliente';
  };

  const campanhaTemplates = {
    natal: { nome: 'Natal', emoji: '🎄', data: '25/12', msg: 'Feliz Natal, {NOME}! 🎄 Obrigado(a) pela confiança esse ano. {DESCONTO}' },
    ano_novo: { nome: 'Ano Novo', emoji: '✨', data: '01/01', msg: 'Feliz Ano Novo, {NOME}! ✨ Que venha um ano incrível!' },
    aniversario: { nome: 'Aniversário', emoji: '🎂', data: 'Auto', msg: '{NOME}! Feliz aniversário 🎉 Deixei um presente: {DESCONTO} válido até {VALIDADE}' },
    dia_criancas: { nome: 'Dia das Crianças', emoji: '🎈', data: '12/10', msg: 'Feliz Dia das Crianças! 🎈 {DESCONTO}' },
    especial: { nome: 'Data Especial', emoji: '⭐', data: 'Custom', msg: '' }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-900">{assinante?.negocio || 'ClienteJá'}</h1>
            <p className="text-sm text-slate-500">{assinante?.nome}</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowConfig(true)} variant="ghost" size="sm" data-testid="btn-config">
              <Settings size={16} />
            </Button>
            <Button onClick={() => setShowNovoCliente(true)} variant="outline" size="sm" data-testid="btn-novo-cliente">
              <Plus size={16} /> Cliente
            </Button>
            <Button onClick={() => setShowNovaVenda(true)} className="bg-teal-600 hover:bg-teal-700" size="sm" data-testid="btn-nova-venda">
              <Plus size={16} /> Venda
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Aniversariantes Alert */}
        {aniversariantes.length > 0 && (
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6" data-testid="aniversariantes-alert">
            <div className="flex items-center gap-3 mb-3">
              <Cake className="text-purple-600" size={20} />
              <span className="font-semibold text-purple-900">🎂 Aniversariantes da semana</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {aniversariantes.map(a => (
                <button
                  key={a.cliente_id}
                  onClick={() => enviarAniversario(a)}
                  className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-purple-200 hover:bg-purple-100 transition-colors"
                >
                  <span className="font-medium text-slate-700">{a.nome}</span>
                  <span className="text-xs text-purple-600">
                    {a.dias_para_aniversario === 0 ? 'HOJE!' : `em ${a.dias_para_aniversario}d`}
                  </span>
                  <Send size={14} className="text-purple-500" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border" data-testid="stat-clientes">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Users size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.totalClientes}</p>
                <p className="text-xs text-slate-500">Clientes</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 border" data-testid="stat-a-receber">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <DollarSign size={20} className="text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{formatCurrency(stats.aReceber)}</p>
                <p className="text-xs text-slate-500">A receber</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 border" data-testid="stat-hoje">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock size={20} className="text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.vencendoHoje}</p>
                <p className="text-xs text-slate-500">Vencem hoje</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 border" data-testid="stat-atrasadas">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <AlertCircle size={20} className="text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.atrasadas}</p>
                <p className="text-xs text-slate-500">Atrasadas</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="parcelas" className="space-y-4">
          <TabsList className="bg-white border">
            <TabsTrigger value="parcelas" data-testid="tab-parcelas">
              <DollarSign size={16} /> Parcelas
            </TabsTrigger>
            <TabsTrigger value="clientes" data-testid="tab-clientes">
              <Users size={16} /> Clientes
            </TabsTrigger>
            <TabsTrigger value="vendas" data-testid="tab-vendas">
              <TrendingUp size={16} /> Vendas
            </TabsTrigger>
            <TabsTrigger value="campanhas" data-testid="tab-campanhas">
              <Megaphone size={16} /> Campanhas
            </TabsTrigger>
          </TabsList>

          {/* PARCELAS TAB */}
          <TabsContent value="parcelas">
            <div className="bg-white rounded-xl border">
              <div className="p-4 border-b">
                <h2 className="font-semibold text-slate-900">Parcelas pendentes</h2>
              </div>
              
              <div className="divide-y">
                {parcelas.filter(p => p.status !== 'pago').length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    Nenhuma parcela pendente 🎉
                  </div>
                ) : (
                  parcelas
                    .filter(p => p.status !== 'pago')
                    .sort((a, b) => a.data_vencimento.localeCompare(b.data_vencimento))
                    .map((parcela) => {
                      const hoje = new Date().toISOString().split('T')[0];
                      const isAtrasada = parcela.data_vencimento < hoje;
                      const isHoje = parcela.data_vencimento === hoje;
                      
                      return (
                        <div 
                          key={parcela.parcela_id} 
                          data-testid={`parcela-${parcela.parcela_id}`}
                          className={`p-4 flex items-center justify-between hover:bg-slate-50 ${isAtrasada ? 'bg-red-50' : ''}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className={`w-2 h-2 rounded-full ${isAtrasada ? 'bg-red-500' : isHoje ? 'bg-amber-500' : 'bg-green-500'}`} />
                            <div>
                              <p className="font-medium text-slate-900">{getClienteName(parcela.cliente_id)}</p>
                              <p className="text-sm text-slate-500">
                                Parcela {parcela.numero} • {formatDate(parcela.data_vencimento)}
                                {isAtrasada && <span className="text-red-500 ml-2">• ATRASADA</span>}
                                {isHoje && <span className="text-amber-500 ml-2">• VENCE HOJE</span>}
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3">
                            <span className="font-bold text-slate-900">{formatCurrency(parcela.valor)}</span>
                            
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`btn-lembrete-${parcela.parcela_id}`}
                              onClick={() => enviarLembrete(parcela)}
                              className="text-teal-600 border-teal-200 hover:bg-teal-50"
                            >
                              <Send size={14} /> Lembrar
                            </Button>
                            
                            <Button
                              size="sm"
                              data-testid={`btn-pago-${parcela.parcela_id}`}
                              onClick={() => marcarComoPago(parcela.parcela_id)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <Check size={14} /> Pago
                            </Button>
                          </div>
                        </div>
                      );
                    })
                )}
              </div>
            </div>
          </TabsContent>

          {/* CLIENTES TAB */}
          <TabsContent value="clientes">
            <div className="bg-white rounded-xl border">
              <div className="p-4 border-b flex items-center justify-between">
                <h2 className="font-semibold text-slate-900">Meus clientes ({clientes.length}/50)</h2>
                <Button size="sm" onClick={() => setShowNovoCliente(true)} className="bg-teal-600 hover:bg-teal-700">
                  <Plus size={14} /> Novo cliente
                </Button>
              </div>
              
              <div className="divide-y">
                {clientes.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    Nenhum cliente cadastrado ainda
                  </div>
                ) : (
                  clientes.map((cliente) => (
                    <div key={cliente.cliente_id} data-testid={`cliente-${cliente.cliente_id}`} className="p-4 flex items-center justify-between hover:bg-slate-50">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center text-teal-600 font-semibold">
                          {cliente.nome?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{cliente.nome}</p>
                          <p className="text-sm text-slate-500">{cliente.telefone}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {cliente.data_nascimento && (
                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full flex items-center gap-1">
                            <Gift size={12} /> {formatDate(cliente.data_nascimento)}
                          </span>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => {
                          setClienteSelecionado(cliente);
                          setNovaVenda({ ...novaVenda, cliente_id: cliente.cliente_id });
                          setShowNovaVenda(true);
                        }}>
                          <Plus size={14} /> Venda
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>

          {/* VENDAS TAB */}
          <TabsContent value="vendas">
            <div className="bg-white rounded-xl border">
              <div className="p-4 border-b flex items-center justify-between">
                <h2 className="font-semibold text-slate-900">Minhas vendas</h2>
                <Button size="sm" onClick={() => setShowNovaVenda(true)} className="bg-teal-600 hover:bg-teal-700">
                  <Plus size={14} /> Nova venda
                </Button>
              </div>
              
              <div className="divide-y">
                {vendas.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    Nenhuma venda registrada ainda
                  </div>
                ) : (
                  vendas.map((venda) => (
                    <div key={venda.venda_id} data-testid={`venda-${venda.venda_id}`} className="p-4 flex items-center justify-between hover:bg-slate-50">
                      <div>
                        <p className="font-medium text-slate-900">{venda.produto}</p>
                        <p className="text-sm text-slate-500">
                          {getClienteName(venda.cliente_id)} • {venda.parcelas}x de {formatCurrency(venda.valor_final / venda.parcelas)}
                        </p>
                      </div>
                      
                      <div className="text-right">
                        <p className="font-bold text-slate-900">{formatCurrency(venda.valor_final)}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          venda.status === 'quitada' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {venda.status === 'quitada' ? 'Quitada' : 'Em andamento'}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>

          {/* CAMPANHAS TAB */}
          <TabsContent value="campanhas">
            <div className="space-y-4">
              {/* Quick Templates */}
              <div className="bg-white rounded-xl border p-4">
                <h3 className="font-semibold text-slate-900 mb-4">📅 Campanhas Rápidas</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {Object.entries(campanhaTemplates).map(([key, t]) => (
                    <button
                      key={key}
                      onClick={() => {
                        setNovaCampanha({
                          ...novaCampanha,
                          tipo: key,
                          nome: t.nome,
                          mensagem: t.msg
                        });
                        setShowNovaCampanha(true);
                      }}
                      className="p-4 rounded-xl border border-slate-200 hover:border-teal-300 hover:bg-teal-50 transition-all text-center"
                    >
                      <span className="text-2xl block mb-1">{t.emoji}</span>
                      <span className="text-sm font-medium text-slate-700">{t.nome}</span>
                      <span className="text-xs text-slate-400 block">{t.data}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Existing Campaigns */}
              <div className="bg-white rounded-xl border">
                <div className="p-4 border-b flex items-center justify-between">
                  <h2 className="font-semibold text-slate-900">Minhas Campanhas</h2>
                  <Button size="sm" onClick={() => setShowNovaCampanha(true)} className="bg-teal-600 hover:bg-teal-700">
                    <Plus size={14} /> Nova campanha
                  </Button>
                </div>
                
                <div className="divide-y">
                  {campanhas.length === 0 ? (
                    <div className="p-8 text-center text-slate-500">
                      Nenhuma campanha criada ainda
                    </div>
                  ) : (
                    campanhas.map((camp) => (
                      <div key={camp.campanha_id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{campanhaTemplates[camp.tipo]?.emoji || '📢'}</span>
                          <div>
                            <p className="font-medium text-slate-900">{camp.nome}</p>
                            <p className="text-sm text-slate-500">
                              {camp.enviada ? `Enviada para ${camp.clientes_alcancados} clientes` : 'Não enviada'}
                            </p>
                          </div>
                        </div>
                        
                        {!camp.enviada && (
                          <Button
                            size="sm"
                            onClick={() => executarCampanha(camp.campanha_id)}
                            className="bg-purple-600 hover:bg-purple-700"
                          >
                            <Send size={14} /> Enviar
                          </Button>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* MODAL: Novo Cliente */}
      {showNovoCliente && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6" data-testid="modal-novo-cliente">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-900">Novo cliente</h2>
              <button onClick={() => setShowNovoCliente(false)} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleNovoCliente} className="space-y-4">
              <div>
                <Label>Nome *</Label>
                <Input data-testid="input-cliente-nome" placeholder="Maria Silva" value={novoCliente.nome} onChange={(e) => setNovoCliente({...novoCliente, nome: e.target.value})} required />
              </div>

              <div>
                <Label>WhatsApp *</Label>
                <Input data-testid="input-cliente-telefone" placeholder="11 99999-9999" value={novoCliente.telefone} onChange={(e) => setNovoCliente({...novoCliente, telefone: e.target.value})} required />
              </div>

              <div>
                <Label>Data de nascimento</Label>
                <Input data-testid="input-cliente-nascimento" type="date" value={novoCliente.data_nascimento} onChange={(e) => setNovoCliente({...novoCliente, data_nascimento: e.target.value})} />
              </div>

              <div className="flex items-center gap-2">
                <input type="checkbox" id="consentimento" data-testid="input-cliente-consentimento" checked={novoCliente.consentimento_mensagens} onChange={(e) => setNovoCliente({...novoCliente, consentimento_mensagens: e.target.checked})} className="rounded border-slate-300" />
                <Label htmlFor="consentimento" className="text-sm font-normal">Cliente autorizou receber mensagens</Label>
              </div>

              <Button type="submit" data-testid="btn-salvar-cliente" className="w-full bg-teal-600 hover:bg-teal-700">Salvar cliente</Button>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: Nova Venda */}
      {showNovaVenda && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6" data-testid="modal-nova-venda">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-900">Nova venda</h2>
              <button onClick={() => { setShowNovaVenda(false); setClienteSelecionado(null); }} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleNovaVenda} className="space-y-4">
              <div>
                <Label>Cliente *</Label>
                {clienteSelecionado ? (
                  <div className="p-3 bg-teal-50 rounded-lg text-teal-700 font-medium">{clienteSelecionado.nome}</div>
                ) : (
                  <select data-testid="select-venda-cliente" className="w-full border rounded-lg p-2" value={novaVenda.cliente_id} onChange={(e) => setNovaVenda({...novaVenda, cliente_id: e.target.value})} required>
                    <option value="">Selecione...</option>
                    {clientes.map(c => (<option key={c.cliente_id} value={c.cliente_id}>{c.nome}</option>))}
                  </select>
                )}
              </div>

              <div>
                <Label>Produto/Serviço *</Label>
                <Input data-testid="input-venda-produto" placeholder="Ex: Móveis planejados" value={novaVenda.produto} onChange={(e) => setNovaVenda({...novaVenda, produto: e.target.value})} required />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Valor total *</Label>
                  <Input data-testid="input-venda-valor" type="number" step="0.01" placeholder="1500.00" value={novaVenda.valor_total} onChange={(e) => setNovaVenda({...novaVenda, valor_total: e.target.value})} required />
                </div>
                <div>
                  <Label>Parcelas</Label>
                  <select data-testid="select-venda-parcelas" className="w-full border rounded-lg p-2 h-10" value={novaVenda.parcelas} onChange={(e) => setNovaVenda({...novaVenda, parcelas: e.target.value})}>
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => (<option key={n} value={n}>{n}x</option>))}
                  </select>
                </div>
              </div>

              <div>
                <Label>Primeiro pagamento *</Label>
                <Input data-testid="input-venda-data" type="date" value={novaVenda.data_primeiro_pagamento} onChange={(e) => setNovaVenda({...novaVenda, data_primeiro_pagamento: e.target.value})} required />
              </div>

              {novaVenda.valor_total && novaVenda.parcelas > 1 && (
                <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
                  {novaVenda.parcelas}x de {formatCurrency(parseFloat(novaVenda.valor_total) / novaVenda.parcelas)}
                </div>
              )}

              <Button type="submit" data-testid="btn-salvar-venda" className="w-full bg-teal-600 hover:bg-teal-700">Criar venda e gerar parcelas</Button>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: Nova Campanha */}
      {showNovaCampanha && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 my-8" data-testid="modal-nova-campanha">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-900">
                {campanhaTemplates[novaCampanha.tipo]?.emoji} {novaCampanha.nome || 'Nova Campanha'}
              </h2>
              <button onClick={() => setShowNovaCampanha(false)} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleNovaCampanha} className="space-y-4">
              <div>
                <Label>Tipo</Label>
                <select className="w-full border rounded-lg p-2" value={novaCampanha.tipo} onChange={(e) => {
                  const t = campanhaTemplates[e.target.value];
                  setNovaCampanha({...novaCampanha, tipo: e.target.value, nome: t.nome, mensagem: t.msg});
                }}>
                  {Object.entries(campanhaTemplates).map(([k, v]) => (
                    <option key={k} value={k}>{v.emoji} {v.nome}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label>Nome da campanha *</Label>
                <Input placeholder="Natal 2025" value={novaCampanha.nome} onChange={(e) => setNovaCampanha({...novaCampanha, nome: e.target.value})} required />
              </div>

              <div>
                <Label>Mensagem *</Label>
                <Textarea 
                  placeholder="Use {NOME}, {DESCONTO}, {VALIDADE}, {ITEM}" 
                  value={novaCampanha.mensagem} 
                  onChange={(e) => setNovaCampanha({...novaCampanha, mensagem: e.target.value})} 
                  rows={3}
                  required
                />
                <p className="text-xs text-slate-400 mt-1">Variáveis: {'{NOME}'}, {'{DESCONTO}'}, {'{VALIDADE}'}, {'{ITEM}'}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Desconto (opcional)</Label>
                  <div className="flex gap-2">
                    <select className="border rounded-lg p-2 w-20" value={novaCampanha.desconto_tipo} onChange={(e) => setNovaCampanha({...novaCampanha, desconto_tipo: e.target.value})}>
                      <option value="">-</option>
                      <option value="percentual">%</option>
                      <option value="valor">R$</option>
                    </select>
                    <Input type="number" placeholder="10" value={novaCampanha.desconto_valor} onChange={(e) => setNovaCampanha({...novaCampanha, desconto_valor: e.target.value})} />
                  </div>
                </div>
                <div>
                  <Label>Válido até</Label>
                  <Input type="date" value={novaCampanha.desconto_validade} onChange={(e) => setNovaCampanha({...novaCampanha, desconto_validade: e.target.value})} />
                </div>
              </div>

              <div>
                <Label>Item específico para desconto</Label>
                <Input placeholder="Ex: Corte de cabelo" value={novaCampanha.item_especifico} onChange={(e) => setNovaCampanha({...novaCampanha, item_especifico: e.target.value})} />
              </div>

              <div>
                <Label>Público</Label>
                <select className="w-full border rounded-lg p-2" value={novaCampanha.publico} onChange={(e) => setNovaCampanha({...novaCampanha, publico: e.target.value})}>
                  <option value="todos">Todos os clientes</option>
                  <option value="tag">Por tag</option>
                  <option value="selecionados">Selecionar manualmente</option>
                </select>
              </div>

              <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700">
                <Megaphone size={16} /> Criar campanha
              </Button>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: Configurações */}
      {showConfig && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 my-8" data-testid="modal-config">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-900">⚙️ Configurações</h2>
              <button onClick={() => setShowConfig(false)} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-6">
              {/* WhatsApp / Z-API */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Phone size={18} /> WhatsApp
                </h3>
                
                <div>
                  <Label>Modo de envio</Label>
                  <select className="w-full border rounded-lg p-2" value={config.modo_envio} onChange={(e) => setConfig({...config, modo_envio: e.target.value})}>
                    <option value="semi">Semi-automático (abre WhatsApp para você enviar)</option>
                    <option value="auto">Automático (Z-API envia direto)</option>
                  </select>
                </div>

                {config.modo_envio === 'auto' && (
                  <>
                    <div>
                      <Label>Z-API Instance ID</Label>
                      <Input placeholder="Seu Instance ID" value={config.zapi_instance_id} onChange={(e) => setConfig({...config, zapi_instance_id: e.target.value})} />
                    </div>
                    <div>
                      <Label>Z-API Token</Label>
                      <Input type="password" placeholder="Seu Token" value={config.zapi_token} onChange={(e) => setConfig({...config, zapi_token: e.target.value})} />
                    </div>
                  </>
                )}

                <div className="space-y-2">
                  <Label>Lembretes automáticos</Label>
                  <div className="flex flex-wrap gap-2">
                    <label className="flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer hover:bg-slate-50">
                      <input type="checkbox" checked={config.lembrete_3dias} onChange={(e) => setConfig({...config, lembrete_3dias: e.target.checked})} />
                      <span className="text-sm">3 dias antes</span>
                    </label>
                    <label className="flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer hover:bg-slate-50">
                      <input type="checkbox" checked={config.lembrete_dia} onChange={(e) => setConfig({...config, lembrete_dia: e.target.checked})} />
                      <span className="text-sm">No dia</span>
                    </label>
                    <label className="flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer hover:bg-slate-50">
                      <input type="checkbox" checked={config.lembrete_atraso} onChange={(e) => setConfig({...config, lembrete_atraso: e.target.checked})} />
                      <span className="text-sm">Após atraso</span>
                    </label>
                  </div>
                  
                  {config.modo_envio === 'auto' && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                      <p className="font-medium text-green-800 mb-1">🤖 Automação Ativa</p>
                      <p className="text-green-700 text-xs">
                        Com Z-API configurado, os lembretes são enviados automaticamente:
                      </p>
                      <ul className="text-green-700 text-xs mt-1 list-disc list-inside">
                        <li>3 dias antes do vencimento</li>
                        <li>No dia do vencimento</li>
                        <li>1 dia após o atraso</li>
                        <li>Aniversários (no dia + 3 dias antes)</li>
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              {/* Cobrança */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <CreditCard size={18} /> Forma de cobrança
                </h3>
                
                <div>
                  <Label>Como você cobra?</Label>
                  <select className="w-full border rounded-lg p-2" value={config.forma_cobranca} onChange={(e) => setConfig({...config, forma_cobranca: e.target.value})}>
                    <option value="manual">Manual (sem info automática)</option>
                    <option value="pix">PIX</option>
                    <option value="boleto">Boleto</option>
                    <option value="cartao">Link de cartão</option>
                    <option value="asaas">Asaas (link de pagamento)</option>
                  </select>
                </div>

                {config.forma_cobranca === 'pix' && (
                  <div className="space-y-3">
                    <div>
                      <Label>Tipo da chave PIX</Label>
                      <select className="w-full border rounded-lg p-2" value={config.pix_tipo} onChange={(e) => setConfig({...config, pix_tipo: e.target.value})}>
                        <option value="cpf">CPF</option>
                        <option value="cnpj">CNPJ</option>
                        <option value="email">Email</option>
                        <option value="telefone">Telefone</option>
                        <option value="aleatoria">Chave aleatória</option>
                      </select>
                    </div>
                    <div>
                      <Label>Chave PIX</Label>
                      <Input placeholder="Sua chave PIX" value={config.pix_chave} onChange={(e) => setConfig({...config, pix_chave: e.target.value})} />
                    </div>
                  </div>
                )}

                {config.forma_cobranca === 'boleto' && (
                  <div>
                    <Label>Link do boleto padrão (ou upload)</Label>
                    <Input placeholder="https://..." value={config.boleto_padrao} onChange={(e) => setConfig({...config, boleto_padrao: e.target.value})} />
                    <p className="text-xs text-slate-400 mt-1">Este link será incluído nas mensagens de cobrança</p>
                  </div>
                )}

                {config.forma_cobranca === 'cartao' && (
                  <div>
                    <Label>Link de pagamento por cartão</Label>
                    <Input placeholder="https://..." value={config.cartao_link} onChange={(e) => setConfig({...config, cartao_link: e.target.value})} />
                  </div>
                )}

                {config.forma_cobranca === 'asaas' && (
                  <div className="space-y-3">
                    <div className="p-4 bg-blue-50 rounded-lg text-sm text-blue-700 border border-blue-200">
                      <p className="font-semibold mb-2">🏦 Integração Asaas (Cobrança Automática)</p>
                      <ol className="list-decimal list-inside space-y-1 text-xs">
                        <li>Crie sua conta gratuita em <a href="https://www.asaas.com/" target="_blank" rel="noopener noreferrer" className="underline font-medium">www.asaas.com</a></li>
                        <li>Após criar, acesse <strong>Configurações → Integrações → API</strong></li>
                        <li>Copie sua <strong>API Key</strong> e envie para nós</li>
                        <li>Faremos a integração automática <strong>(apenas 1x)</strong></li>
                      </ol>
                      <div className="mt-3 p-2 bg-blue-100 rounded text-xs">
                        <strong>✨ Benefício:</strong> Após a integração, cobranças serão geradas automaticamente para todos os seus clientes (PIX, Boleto, Cartão).
                      </div>
                    </div>
                    <div>
                      <Label>Link de pagamento (temporário)</Label>
                      <Input placeholder="https://www.asaas.com/c/..." value={config.asaas_link} onChange={(e) => setConfig({...config, asaas_link: e.target.value})} />
                      <p className="text-xs text-slate-400 mt-1">Use enquanto aguarda a integração automática</p>
                    </div>
                  </div>
                )}
              </div>

              <Button onClick={handleSaveConfig} className="w-full bg-teal-600 hover:bg-teal-700">
                <Check size={16} /> Salvar configurações
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClienteJaPainel;
